import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import {
    addAttributeSelection,
    getSelectedAttributes,
    AttributeState,
    getAttributes,
    getAttributeState,
    getNextPageAttributes,
    clearFilters,
    updateCheckboxes,
    getCheckboxState,
    searchAttributes,
    setCheckboxes,
    getCountries,
} from '@emi/shared/state/filters';
import { Paging } from '@emi/shared/constants';
import { Attribute, CheckboxTreeNode, Page } from '@emi/shared/models';
import { select, Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import * as Constants from '@emi/shared/constants';

@Component({
    selector: 'via-filter-attributes',
    templateUrl: './filter-attributes.component.html',
    styleUrls: ['./filter-attributes.component.scss'],
})
export class FilterAttributesComponent implements OnInit, OnDestroy {
    @Input()
    public applyClicked: EventEmitter<boolean>;

    @Input()
    public cancelClicked: EventEmitter<boolean>;

    @Input()
    public showApplyButton: boolean;

    @Input()
    public isOverlay: boolean;

    @Output()
    public showApplyButtonChange = new EventEmitter<boolean>();

    @ViewChild('scrollArea')
    public scrollArea: ElementRef;

    @ViewChildren('page')
    public pageElements: QueryList<ElementRef>;

    @ViewChildren('pageAnchor')
    public pageAnchorElements: QueryList<ElementRef>;

    private attributesSubscription: Subscription;
    private applyClickedSubscription: Subscription;
    private cancelClickedSubscription: Subscription;
    private checkBoxSubscription: Subscription;
    private stateSubscription: Subscription;
    public attributesTree: Array<CheckboxTreeNode> = [];
    public selectedAttributes: Array<Attribute> = [];
    public stateAttributes: Array<Attribute> = [];
    public loading: boolean;
    public showError = false;
    public errorMessage = '';
    public hasSearchedByText = false;
    public checkedArray: Array<CheckboxTreeNode> = [];
    public pages: Page[] = [];
    public totalSize: number | undefined;
    public eventsSubject: Subject<CheckboxTreeNode[]> = new Subject<CheckboxTreeNode[]>();
    private nextPageId: string | undefined;
    public noResultsMessage = '';
    private isLargeViewport: boolean;

    constructor(private readonly store: Store<AttributeState>, private changeDetector : ChangeDetectorRef) {}

    public onCheckboxStateChange(selectedCheckbox: CheckboxTreeNode): void {
        this.store.dispatch(updateCheckboxes({ checkbox: selectedCheckbox }));
    }

    public onScrollDown(): void {
        if (this.nextPageId && !this.hasSearchedByText) {
            this.store.dispatch(getNextPageAttributes());
        }
    }

    public ngOnInit(): void {
        this.checkBoxSubscription = this.store.pipe(select(getCheckboxState)).subscribe((y) => {
            this.checkedArray = y.checkboxes;
        });

        this.attributesSubscription = this.store.pipe(select(getSelectedAttributes)).subscribe((attributes) => {
            if (attributes.length > 0) {
                if (this.checkedArray.length === 0) {
                    this.store.dispatch(
                        setCheckboxes({
                            checkboxes: attributes.map((attribute) => {
                                return this.buildCheckboxTreeNode(attribute, true);
                            }),
                        })
                    );
                }
                this.selectedAttributes = attributes;
                this.attributesTree = attributes.map((attribute) => {
                    return this.buildCheckboxTreeNode(attribute);
                });
                let nextPage = new Page('', this.attributesTree);
                this.pages.push(nextPage);
                this.eventsSubject.next(nextPage.value);
            }
            this.checkForResults();
        });

        this.stateSubscription = this.store.pipe(select(getAttributeState)).subscribe((state) => {
            this.loading = state.loading;
            this.showError = state.failed;
            this.errorMessage = state.error;
            this.nextPageId = state.metadata?.nextPageId;
            this.totalSize = state.metadata?.totalResults;
            this.pages = [];
            if (this.selectedAttributes.length === 0) {
                this.attributesTree = state.attributes.map((attribute) => {
                    return this.buildCheckboxTreeNode(attribute);
                });
            }
            this.eventsSubject.next(this.attributesTree);
            while (this.attributesTree.length > 0) {
                let newPage = this.attributesTree.splice(0, Paging.PAGE_SIZE);
                this.pages.push(new Page(this.nextPageId, newPage));
            }
            this.stateAttributes = state.attributes;
            this.checkForResults();

            if (!this.isLargeViewport || this.pages.length > Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD) {
                this.scrollToLastPage();
            }
        });

        this.applyClickedSubscription = this.applyClicked.subscribe(() => {
            const attributes = this.checkedArray
                .map((attribute) => new Attribute(attribute.id, attribute.label))
                .sort((a, b) => (a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1));
            this.store.dispatch(addAttributeSelection({ attributes }));
            this.store.dispatch(getCountries());
            this.store.dispatch(clearFilters());
        });

        if (this.isOverlay) {
            this.cancelClickedSubscription = this.cancelClicked.subscribe(() => {
                this.pages = [];
                this.store.dispatch(
                    setCheckboxes({
                        checkboxes: this.selectedAttributes.map((attributeType) => {
                            return this.buildCheckboxTreeNode(attributeType, true);
                        }),
                    })
                );
                const currentSelectableAttributes = this.selectedAttributes.length > 0 ? this.selectedAttributes : this.stateAttributes;
                this.attributesTree = currentSelectableAttributes.map((attributeType) => {
                    return this.buildCheckboxTreeNode(attributeType, this.selectedAttributes.length > 0);
                });
                this.eventsSubject.next(this.attributesTree);
                while (this.attributesTree.length > 0) {
                    let newPage = this.attributesTree.splice(0, Paging.PAGE_SIZE);
                    this.pages.push(new Page(this.nextPageId, newPage));
                }
            });
        }

        if (this.selectedAttributes.length === 0 && this.stateAttributes.length === 0) {
            this.store.dispatch(getAttributes());
        }
    }

    public buildCheckboxTreeNode(attribute: Attribute, completed?: boolean): CheckboxTreeNode {
        if (completed) {
            return new CheckboxTreeNode(attribute.id, attribute.value, 'checked');
        }
        const existingAttribute = this.checkedArray.find((x) => x.id === attribute.id);
        return new CheckboxTreeNode(attribute.id, attribute.value, existingAttribute ? 'checked' : 'unchecked');
    }

    public searchFiltersByText(text: string): void {
        this.hasSearchedByText = true;
        this.store.dispatch(searchAttributes({ searchText: text, delay: Constants.GlobalVariables.FILTER_SEARCH_DELAY }));
    }

    public resetSearch(): void {
        this.hasSearchedByText = false;
        this.store.dispatch(searchAttributes({ searchText: '', delay: 0 }));
    }

    public selectionChanged(): void {
        this.showApplyButtonChange.emit(this.selectedAttributes.length !== this.checkedArray.length && this.checkedArray.length > 0);
    }

    public checkForResults(): void {
        if (!this.loading && this.stateAttributes.length == 0 && !this.hasSearchedByText && this.selectedAttributes.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.ZERO_SKUS;
            return;
        }
        if (!this.loading && this.stateAttributes.length == 0 && this.hasSearchedByText && this.selectedAttributes.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.NO_SEARCH_RESULTS;
            return;
        }
        this.noResultsMessage = '';
    }

    public scrollToLastPage(): void {
        if (this.pageAnchorElements && this.pageAnchorElements.last && this.pageAnchorElements.last.nativeElement) {
            // TODO: investigate alternative to setTimeout
            setTimeout(() => {
                if (!this.isLargeViewport) {
                    this.pageAnchorElements.last.nativeElement.scrollIntoView();
                } else {
                    const pageAnchorIndex = this.pageAnchorElements.length - Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD;
                    this.pageAnchorElements.get(pageAnchorIndex)?.nativeElement.scrollIntoView();
                }
            }, 0);
        }
    }

    public ngOnDestroy(): void {
        if (this.hasSearchedByText && this.selectedAttributes.length === 0) {
            this.store.dispatch(searchAttributes({ searchText: '', delay: 0 }));
        }
        this.checkBoxSubscription.unsubscribe();
        this.stateSubscription.unsubscribe();
        this.attributesSubscription.unsubscribe();
        this.applyClickedSubscription.unsubscribe();
        if (this.isOverlay) {
            this.cancelClickedSubscription.unsubscribe();
        }
        this.showApplyButtonChange.emit(false);
    }

    public checkScrollArea(): void {
        this.changeDetector.detectChanges();
                
        const pageHeight = this.pageElements.first.nativeElement.clientHeight;
        const scrollAreaHeight = this.scrollArea.nativeElement.clientHeight;
        const scrollDistance = this.scrollArea.nativeElement.scrollHeight;

        if (pageHeight <= scrollAreaHeight && this.totalSize && this.totalSize > Paging.PAGE_SIZE) {
            this.isLargeViewport = true;

            if (scrollDistance <= scrollAreaHeight) { // Load another page only if there is no scrollbar
                this.onScrollDown();
            }
        }
    }
}
